import { useEffect, useState } from "react";
import { CurrencyDollar, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { logConversion } from "../lib/api";
import { useDisplayCurrency } from "../lib/currency";

/**
 * Manual "Log conversion" modal — fires POST /api/conversions with source=manual.
 */
export default function LogConversionDialog({
  open,
  onOpenChange,
  adSetId,
  platform,
  variantId,
  variantLetter,
  onLogged,
}) {
  const workspaceCcy = useDisplayCurrency(open);
  const [value, setValue] = useState("");
  const [currency, setCurrency] = useState(workspaceCcy);
  const [externalId, setExternalId] = useState("");
  const [saving, setSaving] = useState(false);

  // Sync default to workspace currency whenever the dialog opens.
  useEffect(() => {
    if (open) setCurrency(workspaceCcy);
  }, [open, workspaceCcy]);

  const submit = async () => {
    if (!adSetId || !platform || !variantId) return;
    setSaving(true);
    try {
      await logConversion({
        ad_set_id: adSetId,
        platform,
        variant_id: variantId,
        value: parseFloat(value || "0") || 0,
        currency: currency.trim().toUpperCase() || workspaceCcy || "USD",
        external_id: externalId.trim() || null,
        source: "manual",
      });
      toast.success(
        `Conversion logged · variant ${variantLetter || variantId.slice(0, 6)}`,
      );
      setValue("");
      setExternalId("");
      onOpenChange(false);
      onLogged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log conversion");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0a0a0a] border border-white/10 rounded-none max-w-md"
        data-testid="log-conversion-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <CurrencyDollar
              size={22}
              weight="fill"
              className="text-[#ccff00]"
            />
            Log conversion
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Record a real sale for{" "}
            <span className="text-[#ccff00] font-mono">
              {platform?.toUpperCase()} · variant {variantLetter || "?"}
            </span>
            . This will boost the winner-by-revenue rank.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div>
            <div className="label-mono mb-2">value</div>
            <Input
              data-testid="conv-value-input"
              type="number"
              step="0.01"
              min="0"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="49.99"
              className="rounded-none bg-[#121212] border-white/10 h-11 font-mono"
            />
          </div>
          <div>
            <div className="label-mono mb-2">currency</div>
            <Input
              data-testid="conv-currency-input"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              placeholder={workspaceCcy}
              maxLength={4}
              className="rounded-none bg-[#121212] border-white/10 h-11 font-mono"
            />
            <div className="text-[10px] text-zinc-500 font-mono mt-1">
              defaults to workspace currency ({workspaceCcy}) · FX applied on save
            </div>
          </div>
          <div>
            <div className="label-mono mb-2">order / external id (optional, dedupes)</div>
            <Input
              data-testid="conv-external-id-input"
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder="ORDER-12345"
              className="rounded-none bg-[#121212] border-white/10 h-11 font-mono text-xs"
            />
          </div>
        </div>
        <button
          data-testid="conv-submit-btn"
          onClick={submit}
          disabled={saving}
          className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold py-3 px-4 flex items-center justify-center gap-2 transition-colors"
        >
          {saving ? (
            <>
              <Sparkle size={14} weight="fill" className="animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <CurrencyDollar size={14} weight="fill" />
              Log conversion
            </>
          )}
        </button>
      </DialogContent>
    </Dialog>
  );
}
